/**
 * Processing setup (F6):
 *   /process-runs/steps  GET — the step registry: code, label, lossLimitPct (process-runs:read)
 *   /yield-bands         GET ?cropId&cropVarietyId&stepCode&equipmentId&latest · POST (a new version; insert-only)
 *   /machines            GET ?siteId — processing machines with their step codes (equipment:read)
 *   /crop-varieties      GET ?cropId (crops:read)
 * Yield bands and machines are raw SQL rows (snake_case); percents and kg as decimal strings.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, num, relId, str, strOrNull } from './mappers'
import type { YieldBandBody } from '../model/yieldBandForm'

const STATIC = { staleTime: 10 * 60_000 }

// ============================== step registry ==============================

export interface StepOption {
  code: string
  label: string
  /** The step's loss limit, % of kilos in — used when a band carries none. */
  lossLimitPct: string
}
export const mapStep = (r: Record<string, unknown>): StepOption => ({
  code: str(r.code),
  label: str(r.label, str(r.code)),
  lossLimitPct: str(r.lossLimitPct ?? r.loss_limit_pct, '0.50'),
})
export function useSteps() {
  return useQuery({ queryKey: [...ck.steps], queryFn: () => getList('/process-runs/steps', undefined, mapStep), ...STATIC })
}

// ============================== yield bands ==============================

export interface YieldBandRow {
  id: string
  cropId: string
  cropName: string | null
  cropVarietyId: string | null
  varietyName: string | null
  stepCode: string
  equipmentId: string | null
  equipmentCode: string | null
  equipmentName: string | null
  version: number
  effectiveFrom: string
  minPct: string
  maxPct: string
  minFlushKg: string | null
  minInputKg: string | null
  /** The band's own loss limit; empty = the step's (registry). */
  maxUnaccountedPct: string | null
  source: string
  changeNote: string | null
}
export const mapYieldBand = (r: Record<string, unknown>): YieldBandRow => ({
  id: str(r.id),
  cropId: str(r.crop_id),
  cropName: strOrNull(r.crop_name),
  cropVarietyId: strOrNull(r.crop_variety_id),
  varietyName: strOrNull(r.variety_name),
  stepCode: str(r.step_code),
  equipmentId: strOrNull(r.equipment_id),
  equipmentCode: strOrNull(r.equipment_code),
  equipmentName: strOrNull(r.equipment_name),
  version: num(r.version, 1),
  effectiveFrom: str(r.effective_from).slice(0, 10),
  minPct: str(r.min_pct),
  maxPct: str(r.max_pct),
  minFlushKg: strOrNull(r.min_flush_kg),
  minInputKg: strOrNull(r.min_input_kg),
  maxUnaccountedPct: strOrNull(r.max_unaccounted_pct),
  source: str(r.source, 'MANUAL'),
  changeNote: strOrNull(r.change_note),
})

export interface YieldBandFilters extends PageParams {
  cropId?: string
  cropVarietyId?: string
  stepCode?: string
  equipmentId?: string
  /** Only the newest version of each crop, variety, step and machine. */
  latest?: boolean
}
export function useYieldBands(f: YieldBandFilters, enabled = true) {
  return usePageQuery([...ck.yieldBands, 'list', f], () => getPage('/yield-bands', f, mapYieldBand), { enabled })
}
/** `inline`: the form shows the refusal itself (no toast). */
export function useLoadYieldBand({ inline = false } = {}) {
  return useConsoleMutation<YieldBandBody>({
    mutationFn: (body) => api.post('/yield-bands', body),
    // a machine's settings rows show in the machine register too
    invalidate: [ck.yieldBands, ck.equipment],
    success: 'Yield band saved',
    errorTitle: 'Yield band not saved',
    onError: () => inline,
  })
}

// ============================== machines, varieties ==============================

export interface MachineOption {
  id: string
  code: string
  name: string
  stepCodes: string[]
  isActive: boolean
}
export const mapMachine = (r: Record<string, unknown>): MachineOption => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  stepCodes: arr<unknown>(r.step_codes).map((s) => String(s)),
  isActive: r.is_active === undefined ? true : Boolean(r.is_active),
})
export function useMachines() {
  return useQuery({
    queryKey: [...ck.equipment, 'machines'],
    queryFn: () => getPage('/machines', { page: 1, limit: 200 }, mapMachine).then((p) => p.rows),
    staleTime: 60_000,
  })
}

export interface VarietyOption {
  id: string
  name: string
  cropId: string | null
}
export function useVarieties(cropId: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.reference, 'varieties', cropId],
    enabled: Boolean(cropId),
    queryFn: () =>
      getPage('/crop-varieties', { cropId, page: 1, limit: 100 }, (r): VarietyOption => ({
        id: str(r.id),
        name: str(r.name),
        cropId: relId(r.crop),
      })).then((p) => p.rows),
    ...STATIC,
  })
}
